import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Send, Clock, Flame, Zap, CheckCircle2, Sparkles, Share2, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { getSkillLabel, computeOverallScore } from '../data/skillAreas';
import SkillScorePanel from '../components/SkillScorePanel';

interface Challenge {
  id: string;
  slug: string;
  title: string;
  description: string;
  duration_minutes: number;
  skill_areas: string[];
  difficulty: string;
  prompt_text: string;
  rubric: string;
  estimated_xp: number;
  sort_order: number;
}

interface Attempt {
  challenge_id: string;
  score: number;
  feedback: string;
  skill_deltas: Record<string, number>;
  xp_awarded: number;
  submitted_at: string;
  attempt_date: string;
}

interface GradeResult {
  score: number;
  feedback: string;
  skill_deltas: Record<string, number>;
  xp_awarded: number;
  challenge: Challenge;
}

const DIFFICULTY_STYLE: Record<string, string> = {
  easy: 'text-mint-700 bg-mint-50 border-mint-200',
  medium: 'text-sun-600 bg-sun-50 border-sun-200',
  hard: 'text-coral-600 bg-coral-50 border-coral-200',
};

export default function ChallengePage() {
  useDocumentMeta({
    title: 'AI Challenges — Practice & Get Graded | PromptGPT',
    description: 'Complete daily AI challenges that train real skills — prompting, reasoning, verification, critique, and more. Submit your work, get graded by AI, and watch your skill score climb.',
    canonical: 'https://promptgpt.ai/challenges',
  });

  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [completedToday, setCompletedToday] = useState<Set<string>>(new Set());
  const [skillScores, setSkillScores] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [activeChallenge, setActiveChallenge] = useState<Challenge | null>(null);
  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [gradeResult, setGradeResult] = useState<GradeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prevStreak, setPrevStreak] = useState(0);

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/grade-challenge`;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const authHeaders = async (): Promise<Record<string, string>> => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'apikey': anonKey || '',
      'Authorization': `Bearer ${session?.access_token || anonKey || ''}`,
    };
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(apiUrl, { headers: await authHeaders() });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.error || 'Could not load challenges');
          return;
        }
        const data = await res.json();
        setChallenges(data.challenges || []);
        setAttempts(data.attempts || []);
        setCompletedToday(new Set(data.completedToday || []));
        setSkillScores(data.skillScores || {});
      } catch {
        setError('Could not load challenges');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleSubmit = async () => {
    if (!activeChallenge || submission.trim().length < 10) {
      toast.error('Write at least 10 characters');
      return;
    }
    setSubmitting(true);
    setPrevStreak(profile?.current_streak ?? 0);
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ challengeId: activeChallenge.id, submission: submission.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Submission failed');
        return;
      }
      setGradeResult(data);
      setCompletedToday((prev) => new Set(prev).add(activeChallenge.id));
      setAttempts((prev) => [{
        challenge_id: activeChallenge.id,
        score: data.score,
        feedback: data.feedback,
        skill_deltas: data.skill_deltas,
        xp_awarded: data.xp_awarded,
        submitted_at: new Date().toISOString(),
        attempt_date: new Date().toISOString().slice(0, 10),
      }, ...prev]);

      const newScores = { ...skillScores };
      for (const [area, delta] of Object.entries(data.skill_deltas)) {
        newScores[area] = Math.min(100, (newScores[area] ?? 0) + delta);
      }
      setSkillScores(newScores);
      refreshProfile();
      toast.success('Challenge graded!');
    } catch {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = () => {
    if (!gradeResult || !activeChallenge) return;
    const overall = computeOverallScore(skillScores);
    const text = `I scored ${gradeResult.score} on "${activeChallenge.title}" on PromptGPT. My AI Skill Score is now ${overall}.`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Score copied to clipboard'),
      () => toast.error('Could not copy'),
    );
  };

  const goToNextChallenge = () => {
    const next = challenges.find((c) => !completedToday.has(c.id) && c.id !== activeChallenge?.id);
    if (next) {
      setActiveChallenge(next);
      setGradeResult(null);
      setSubmission('');
    } else {
      navigate('/learn');
    }
  };

  if (loading) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <div className="space-y-4">
          <div className="h-8 w-48 bg-ink-100 rounded-lg animate-pulse" />
          <div className="h-32 bg-ink-100 rounded-2xl animate-pulse" />
          <div className="h-48 bg-ink-100 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Today
        </Link>
        <div className="bg-warm-100 border border-ink-200 rounded-2xl p-10 text-center">
          <h2 className="font-display text-2xl mb-2">Could not load challenges</h2>
          <p className="text-ink-600 text-sm mb-6">{error}</p>
          <Link to="/learn" className="btn-secondary">Back to Today</Link>
        </div>
      </div>
    );
  }

  if (challenges.length === 0) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Today
        </Link>
        <div className="bg-warm-100 border border-ink-200 rounded-2xl p-10 text-center">
          <h2 className="font-display text-2xl mb-2">New challenges coming soon</h2>
          <p className="text-ink-600 text-sm mb-6">Try the daily drill or bring your own problem while we prepare new challenges.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/drills" className="btn-primary">Daily drill</Link>
            <Link to="/practice" className="btn-secondary">Bring Your Own Problem</Link>
          </div>
        </div>
      </div>
    );
  }

  // Active challenge view
  if (activeChallenge) {
    const diffStyle = DIFFICULTY_STYLE[activeChallenge.difficulty] || 'text-ink-500 bg-warm-100 border-ink-200';
    const alreadyDone = completedToday.has(activeChallenge.id);
    const streakIncreased = gradeResult && (profile?.current_streak ?? 0) > prevStreak;

    return (
      <div className="container-app py-10 max-w-2xl">
        <button
          onClick={() => { setActiveChallenge(null); setGradeResult(null); setSubmission(''); }}
          className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> All challenges
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500">Challenge</span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${diffStyle}`}>
              {activeChallenge.difficulty}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-ink-500">
              <Clock className="w-3.5 h-3.5" /> {activeChallenge.duration_minutes} min
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">{activeChallenge.title}</h1>
          <p className="text-ink-600 text-lg leading-relaxed">{activeChallenge.description}</p>
        </div>

        <div className="bg-white border border-ink-100 rounded-2xl p-6 mb-6">
          <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Your task</div>
          <p className="text-ink-800 leading-relaxed text-[17px]">{activeChallenge.prompt_text}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {activeChallenge.skill_areas.map((area) => (
              <span key={area} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                {getSkillLabel(area)}
              </span>
            ))}
          </div>
        </div>

        {gradeResult ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Score reveal with animation */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="bg-white border border-ink-100 rounded-2xl p-6"
            >
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Score</div>
                  <motion.div
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.1, type: 'spring', bounce: 0.5 }}
                    className={`font-display text-7xl font-semibold leading-none ${
                      gradeResult.score >= 90 ? 'text-mint-700' :
                      gradeResult.score >= 70 ? 'text-accent-700' :
                      gradeResult.score >= 50 ? 'text-sun-600' :
                      'text-coral-600'
                    }`}
                  >
                    {gradeResult.score}
                  </motion.div>
                </div>
                <div className="flex items-center gap-1.5 text-accent-700 font-bold text-lg pb-1">
                  <Sparkles className="w-5 h-5" />+{gradeResult.xp_awarded} XP
                </div>
              </div>

              {/* Streak indicator */}
              {streakIncreased && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-2 text-sm text-sun-600 font-medium mb-3"
                >
                  <Flame className="w-4 h-4" />
                  Streak extended to {profile?.current_streak ?? 0} days!
                </motion.div>
              )}

              {/* Overall score update */}
              <div className="flex items-center gap-2 text-sm text-ink-600 mb-4">
                <TrendingUp className="w-4 h-4 text-accent-500" />
                <span>Your AI Skill Score is now <span className="font-bold text-ink-900">{computeOverallScore(skillScores)}</span></span>
              </div>

              {gradeResult.feedback && (
                <p className="text-ink-700 leading-relaxed text-sm border-t border-ink-100 pt-4">{gradeResult.feedback}</p>
              )}
            </motion.div>

            {/* Skills improved */}
            <div className="bg-white border border-ink-100 rounded-2xl p-6">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-4">Skills improved</div>
              <div className="space-y-3">
                {Object.entries(gradeResult.skill_deltas).map(([area, delta]) => (
                  <div key={area} className="flex items-center justify-between">
                    <span className="text-sm text-ink-700">{getSkillLabel(area)}</span>
                    <span className="text-sm font-semibold text-mint-600">+{delta}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={goToNextChallenge}
                className="btn-primary flex-1 justify-center"
              >
                Next challenge <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={handleShare}
                className="btn-secondary"
                title="Copy score to clipboard"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
            <div className="text-center">
              <Link to="/learn" className="text-sm text-ink-500 hover:text-ink-900">Back to Today</Link>
            </div>
          </motion.div>
        ) : alreadyDone ? (
          <div className="bg-warm-100 border border-ink-200 rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-8 h-8 text-mint-600 mx-auto mb-3" />
            <h2 className="font-display text-2xl mb-2">Already completed today</h2>
            <p className="text-ink-600 text-sm mb-6">You already attempted this challenge today. Come back tomorrow or try another one.</p>
            <button
              onClick={() => { setActiveChallenge(null); }}
              className="btn-secondary"
            >
              Browse other challenges
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold block mb-2">
                Write your response
              </label>
              <textarea
                value={submission}
                onChange={(e) => setSubmission(e.target.value)}
                placeholder="Write your prompt, response, or analysis here. Be specific and thorough."
                rows={8}
                className="w-full border border-ink-200 rounded-xl px-4 py-3 text-ink-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition"
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-ink-400">One attempt per challenge per day · graded by AI</p>
                <span className={`text-xs font-mono ${submission.length >= 10 ? 'text-mint-700' : 'text-ink-400'}`}>
                  {submission.length} / 10+
                </span>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || submission.trim().length < 10}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Grading…' : <>Submit for grading <Send className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Challenge list view
  const sortedChallenges = [...challenges].sort((a, b) => {
    const aDone = completedToday.has(a.id) ? 1 : 0;
    const bDone = completedToday.has(b.id) ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    return a.sort_order - b.sort_order;
  });

  return (
    <div className="container-app py-10 max-w-3xl">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Today
      </Link>

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">Challenges</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">Practice a skill. Get graded.</h1>
        <p className="text-ink-600 text-lg leading-relaxed">Each challenge trains specific AI skills. Submit your work and get instant feedback with a score.</p>
      </div>

      {profile && (
        <div className="bg-white border border-ink-100 rounded-2xl p-6 mb-8">
          <SkillScorePanel scores={skillScores} />
        </div>
      )}

      <div className="space-y-3">
        {sortedChallenges.map((c) => {
          const done = completedToday.has(c.id);
          const attempt = attempts.find((a) => a.challenge_id === c.id);
          const diffStyle = DIFFICULTY_STYLE[c.difficulty] || 'text-ink-500 bg-warm-100 border-ink-200';
          return (
            <button
              key={c.id}
              onClick={() => { setActiveChallenge(c); setGradeResult(null); setSubmission(''); }}
              className="w-full text-left bg-white border border-ink-100 hover:border-ink-200 hover:shadow-soft rounded-2xl p-5 transition group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${diffStyle}`}>
                      {c.difficulty}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-ink-500">
                      <Clock className="w-3.5 h-3.5" /> {c.duration_minutes} min
                    </span>
                    {done && (
                      <span className="inline-flex items-center gap-1 text-xs text-mint-600 font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Done today
                      </span>
                    )}
                  </div>
                  <div className="font-display text-xl mb-1">{c.title}</div>
                  <p className="text-sm text-ink-600 leading-relaxed">{c.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {c.skill_areas.map((area) => (
                      <span key={area} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-warm-100 text-ink-600">
                        {getSkillLabel(area)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 pt-1">
                  {attempt ? (
                    <div className="text-center">
                      <div className={`font-display text-2xl font-semibold ${
                        attempt.score >= 90 ? 'text-mint-700' :
                        attempt.score >= 70 ? 'text-accent-700' :
                        attempt.score >= 50 ? 'text-sun-600' :
                        'text-coral-600'
                      }`}>{attempt.score}</div>
                      <div className="text-[10px] text-ink-400 uppercase tracking-wider">Best</div>
                    </div>
                  ) : (
                    <ArrowRight className="w-5 h-5 text-ink-300 group-hover:text-ink-700 transition" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
